// Resolving a MONSTER's saving-throw bonus from more than one source.
//
// Feature 2 (requested 2026-10-18): the initiative row for a goblin, a lich or a
// Compass-forged beast should roll its real save, not a plain d20. But the number
// can come from three different places, and they do not always agree:
//   - D&D Beyond   (a monster stat block the DM pulled from the DDB connector)
//   - Compass/LMCC (a creature forged in the Loremaster's Compass bestiary)
//   - Roll20       (the npc_<ability>_save attribs on the token's own sheet)
//
// The honesty rule from rowSave.ts still holds — a recorded save beats an ability
// mod beats nothing, and the source travels with the bonus so the DM can tell a
// fact from a guess. What is new is CHOOSING when two sources both have an answer.
// The DM's decision (2026-10-18): ask every time by default, but let them set a
// precedence order (1/2/3) in Table settings; once set, precedence decides and we
// stop asking. This module is pure — the lanes that FETCH each source's numbers
// live elsewhere (monsterSaveSources.ts); here we only reason about what we have.
import { abilityMod, type AbilityKey } from "./types";
import { ABILITY_LABEL, sign, type SaveBonus } from "./rowSave";

export type SaveSource = "ddb" | "lmcc" | "roll20";
export const SAVE_SOURCES: SaveSource[] = ["ddb", "lmcc", "roll20"];
export const SAVE_SOURCE_LABEL: Record<SaveSource, string> = { ddb: "D&D Beyond", lmcc: "Compass", roll20: "Roll20" };

/** One source's numeric view of a creature. `saves`/`abilities` are per-ability;
 *  a source that was queried but did not hold this creature has found=false and
 *  contributes no candidate. Kept numeric (not the formatted statBlock string) so
 *  a +0 that is real survives — the same absent-vs-zero trap rowSave.ts guards. */
export interface MonsterSaveData {
  source: SaveSource;
  found: boolean;
  saves?: Partial<Record<AbilityKey, number>>;
  abilities?: Partial<Record<AbilityKey, number>>;
}

/** A single source's answer for one ability: the bonus plus which source it came
 *  from, so the ask-dialog can label each choice and the roll can name its origin. */
export interface SaveCandidate { source: SaveSource; bonus: SaveBonus }

const finite = (v: unknown): number | null => {
  if (v === null || v === undefined || String(v).trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Turn each source's numbers into at most one candidate for the given ability.
 *  Mirrors saveBonusFor's precedence WITHIN a source (recorded save, else ability
 *  mod, else nothing), but never invents a flat d20 — "no source had it" is the
 *  caller's business (see resolveSave → {kind:"none"}). */
export function candidatesFor(ability: AbilityKey, sources: MonsterSaveData[], extra = 0): SaveCandidate[] {
  const add = finite(extra) ?? 0;
  const out: SaveCandidate[] = [];
  for (const s of sources) {
    if (!s.found) continue;
    const save = finite(s.saves?.[ability]);
    if (save !== null) {
      out.push({ source: s.source, bonus: { bonus: save + add, basis: "save", why: `${SAVE_SOURCE_LABEL[s.source]}: ${ABILITY_LABEL[ability]} save ${sign(save)}` } });
      continue;
    }
    const score = finite(s.abilities?.[ability]);
    if (score !== null) {
      out.push({ source: s.source, bonus: { bonus: abilityMod(score) + add, basis: "ability", why: `${SAVE_SOURCE_LABEL[s.source]}: ${ABILITY_LABEL[ability]} ${score} (${sign(abilityMod(score))})` } });
    }
  }
  return out;
}

/** The outcome of reconciling the candidates with the DM's settings:
 *   use  — one answer to roll with (0 or 1 candidate, or precedence decided)
 *   ask  — hand the DM the list and let them pick (the default when 2+ disagree)
 *   none — no source had this creature; the caller falls back to a flat d20. */
export type Resolution =
  | { kind: "use"; candidate: SaveCandidate }
  | { kind: "ask"; candidates: SaveCandidate[] }
  | { kind: "none" };

/** Decide what to do with the candidates a creature's sources produced.
 *
 *  `precedence` is the DM's ordered source preference from Table settings (empty
 *  when they have not set one). `askEveryTime` is the default-on behaviour. */
export function resolveSave(candidates: SaveCandidate[], precedence: SaveSource[], askEveryTime: boolean): Resolution {
  if (candidates.length === 0) return { kind: "none" };
  if (candidates.length === 1) return { kind: "use", candidate: candidates[0] };
  // precedence first: honour the DM's ordered preference, skipping any source
  // this particular creature happens to lack so a preference never dead-ends.
  for (const src of precedence) {
    const hit = candidates.find((c) => c.source === src);
    if (hit) return { kind: "use", candidate: hit };
  }
  // then ask: nothing in precedence applied (it was empty, or named only sources
  // this creature lacks). Ask when that is the DM's default; otherwise take the
  // first candidate, because a DM who turned asking OFF without a usable
  // precedence wants the roll to happen, not a dialog, mid-combat.
  if (askEveryTime) return { kind: "ask", candidates };
  return { kind: "use", candidate: candidates[0] };
}
